import { useMemo, useEffect, useCallback, useState, useRef } from "react";
import "./App.css";
import {
  AuthContext,
  type AppTokens,
  tokenExpired,
  getAccessToken,
} from "./context/auth";
import ServiceWorkerWrapper from "./helper/serviceWorkerWrapper/serviceWorkerWrapper";
import routes from "./App.route";
import useOnlineStatus from "@rehooks/online-status";
import Snackbar from "@mui/material/Snackbar";
import BrandingProvider from "@/branding/BrandingProvider";
import { ConfirmProvider } from "./components/dialog/confirm";
import { useLocation, useNavigate } from "react-router-dom";
import { getTenantFromPath } from "./tenant";
import 'leaflet/dist/leaflet.css';

/* ---------------- storage helpers ---------------- */
function storageKey(tenant: string | null | undefined) {
  return `tokens:${tenant || "_none"}`;
}

function readTokensFor(tenant: string | null | undefined): AppTokens {
  try {
    const raw = localStorage.getItem(storageKey(tenant));
    const parsed = raw ? (JSON.parse(raw) as AppTokens) : null;
    return parsed && !tokenExpired(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeTokensFor(tenant: string | null | undefined, data: AppTokens) {
  if (!data || tokenExpired(data)) {
    localStorage.removeItem(storageKey(tenant));
  } else {
    localStorage.setItem(storageKey(tenant), JSON.stringify(data));
  }
}

function clearTokensFor(tenant: string | null | undefined) {
  localStorage.removeItem(storageKey(tenant));
}

/* Optional: enforce that the token itself carries this tenant (if your JWT has tenant/tid) */
function tenantFromToken(tokens: AppTokens): string | null {
  const raw = getAccessToken(tokens);
  if (!raw) return null;
  const parts = raw.split(".");
  if (parts.length !== 3) return null;
  try {
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(b64 + "===".slice((b64.length + 3) % 4)));
    return payload?.tenant ?? payload?.tid ?? payload?.tenantKey ?? null;
  } catch {
    return null;
  }
}

export default function App() {
  const onlineStatus = useOnlineStatus();
  const location = useLocation();
  const navigate = useNavigate();

  // Derive tenant from the current URL (first segment). Use location.pathname (not window).
  const tenant = useMemo(
    () => getTenantFromPath(location.pathname) ?? "",
    [location.pathname]
  );

  // Track previous tenant to detect changes
  const prevTenantRef = useRef<string | null>(null);
  useEffect(() => {
    prevTenantRef.current = tenant;
  }, [tenant]);

  // Initialize tokens from *this* tenant’s storage
  const [authTokens, setTokensState] = useState<AppTokens>(() =>
    readTokensFor(tenant)
  );

  const setTokens = useCallback(
    (data: AppTokens) => {
      if (!data || tokenExpired(data)) {
        clearTokensFor(tenant);
        setTokensState(null);
      } else {
        writeTokensFor(tenant, data);
        setTokensState(data);
      }
    },
    [tenant]
  );

  const logout = useCallback(() => {
    clearTokensFor(tenant);
    localStorage.removeItem("user_info");
    sessionStorage.clear();
    setTokensState(null);
    // IMPORTANT: don't include `from` here; this is not a protected-origin redirect
    navigate(`/${tenant}/login`, {
      replace: true,
      state: { reason: "logout", needsAuth: false },
    });
  }, [navigate, tenant]);

  /* --- 1) Re-check at mount and whenever tokens change --- */
  useEffect(() => {
    if (authTokens && tokenExpired(authTokens)) logout();
  }, [authTokens, logout]);

  /* --- 2) Re-check when tab regains focus (covers long idle tabs) --- */
  useEffect(() => {
    const onFocus = () => {
      // Don't bounce users off public pages
      if (!authTokens) return;

      const current = readTokensFor(tenant);
      if (!current || tokenExpired(current)) {
        logout();
      }
    };

    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [authTokens, tenant, logout]);

  /* --- 3) React when the tenant segment changes --- */
  useEffect(() => {
    const prevTenant = prevTenantRef.current;
    if (!prevTenant || prevTenant === tenant) return;

    // Clear any stale tokens under the *previous* tenant to avoid cross-tenant reuse
    clearTokensFor(prevTenant);

    // Load tokens for the *new* tenant (if any)
    const nextTokens = readTokensFor(tenant);
    if (!nextTokens) {
      // No valid session under the new tenant → go to that tenant’s login
      setTokensState(null);
      navigate(`/${tenant}/login?reason=tenant_switched`, {
        replace: true,
        state: { reason: "tenant_switched", needsAuth: false },
      });
      return;
    }

    // Optional: enforce tenant claim match to URL
    const claimTenant = tenantFromToken(nextTokens);
    if (claimTenant && claimTenant !== tenant) {
      // Mismatch → treat as invalid for this tenant
      clearTokensFor(tenant);
      setTokensState(null);
      navigate(`/${tenant}/login?reason=tenant_mismatch`, {
        replace: true,
        state: { reason: "tenant_mismatch", needsAuth: false },
      });
      return;
    }

    // Adopt the new tenant’s tokens
    setTokensState(nextTokens);
    // (No navigation needed; user remains on the new tenant route)
  }, [tenant, navigate]);

  const isAuthenticated = !!authTokens;

  return (
    <div className="app-surface">
      <BrandingProvider>
        <ConfirmProvider>
          <AuthContext.Provider
            key={tenant}
            value={{ authTokens, setTokens, logout, isAuthenticated }}
          >
            {routes}
            <ServiceWorkerWrapper />
            <Snackbar
              open={!onlineStatus}
              message="You are offline"
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            />
          </AuthContext.Provider>
        </ConfirmProvider>
      </BrandingProvider>
    </div>
  );
}
